import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, X, Gift } from "lucide-react";
import { submitWaitlist, getReferralCount, validateReferralCode } from "../lib/api";
import { readMarketingConsent, readMetaCookies } from "../lib/consent";

const REF_KEY = "cleany_referred_by";

function generateWaitlistEventId() {
    const ts = Math.floor(Date.now() / 1000);
    const rand = Math.random().toString(36).slice(2, 6).padEnd(4, "0");
    return `waitlist_${ts}_${rand}`;
}

export default function WaitlistDialog({ open, onClose, source = "hero" }) {
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        message: "",
        website: "", // honeypot — should stay empty
    });
    const [refCodeInput, setRefCodeInput] = useState("");
    const [refStatus, setRefStatus] = useState({ state: "idle" }); // idle | checking | valid | invalid
    const refDebounce = useRef(null);
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [referralLink, setReferralLink] = useState("");
    const [referralCode, setReferralCode] = useState("");
    const [refCount, setRefCount] = useState(null);

    // Pre-fill referral code from localStorage (set when visitor lands via ?ref=)
    // — only once per open.
    useEffect(() => {
        if (!open) return;
        try {
            const stored = localStorage.getItem(REF_KEY);
            if (stored) setRefCodeInput(stored);
        } catch { /* ignore */ }
    }, [open]);

    // Debounced live validation of the referral code as the user types.
    useEffect(() => {
        const raw = refCodeInput.trim();
        if (!raw) {
            setRefStatus({ state: "idle" });
            return undefined;
        }
        if (raw.length < 4) {
            setRefStatus({ state: "idle" });
            return undefined;
        }
        setRefStatus({ state: "checking" });
        if (refDebounce.current) clearTimeout(refDebounce.current);
        refDebounce.current = setTimeout(async () => {
            const res = await validateReferralCode(raw);
            if (res.valid) {
                setRefStatus({ state: "valid", referrer: res.referrer_name || null });
            } else {
                setRefStatus({ state: "invalid" });
            }
        }, 350);
        return () => {
            if (refDebounce.current) clearTimeout(refDebounce.current);
        };
    }, [refCodeInput]);

    // Poll referral count after success so user sees live updates if they keep dialog open
    useEffect(() => {
        if (!done || !referralCode) return;
        let cancelled = false;
        const fetchCount = async () => {
            try {
                const r = await getReferralCount(referralCode);
                if (!cancelled) setRefCount(r.count);
            } catch (e) {
                console.warn("Failed to fetch referral count:", e);
            }
        };
        fetchCount();
        const id = setInterval(fetchCount, 15000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [done, referralCode]);

    if (!open) return null;

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.company || !form.email || !form.phone) {
            toast.error("Udfyld navn, firma, email og telefon.");
            return;
        }
        if (!acceptPrivacy) {
            toast.error("Du skal acceptere privatlivspolitikken for at tilmelde dig.");
            return;
        }
        // If user typed a code but it's invalid, block submit so they can fix it.
        if (refCodeInput.trim() && refStatus.state === "invalid") {
            toast.error("Referral-koden er ugyldig. Ret den eller fjern den for at tilmelde dig.");
            return;
        }
        if (refStatus.state === "checking") {
            toast.error("Validerer referral-kode — vent et øjeblik.");
            return;
        }
        setLoading(true);
        const eventId = generateWaitlistEventId();
        const marketingConsent = readMarketingConsent();
        const { fbp, fbc } = readMetaCookies();
        try {
            // Use whichever code is on the form (which we may have pre-filled
            // from localStorage already). Fallback to localStorage just in
            // case the user cleared the field.
            const referredBy = (() => {
                if (refStatus.state === "valid") return refCodeInput.trim();
                try {
                    return localStorage.getItem(REF_KEY) || null;
                } catch {
                    return null;
                }
            })();
            const res = await submitWaitlist({
                ...form,
                source,
                referred_by: referredBy,
                event_id: eventId,
                marketing_consent: marketingConsent,
                fbp,
                fbc,
            });
            const code = res?.referral_code || "";
            setReferralCode(code);
            setReferralLink(`${window.location.origin}/?ref=${code}`);
            setDone(true);
            // Push waitlist_submit to dataLayer AFTER server confirmed success.
            // Uses the SAME event_id that went to the backend (→ Meta CAPI)
            // so browser-pixel + server-pixel deduplicate on Meta's side.
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "waitlist_submit",
                event_id: eventId,
            });
            toast.success("Tak! Du er på ventelisten.");
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error("For mange forsøg — prøv igen om et minut.");
            } else {
                const msg = err?.response?.data?.detail || "Kunne ikke tilmelde dig. Prøv igen.";
                toast.error(typeof msg === "string" ? msg : "Kunne ikke tilmelde dig. Prøv igen.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
            data-testid="waitlist-dialog"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                onClick={onClose}
                data-testid="waitlist-dialog-backdrop"
            />
            <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
                <div className="px-7 pt-6 pb-4 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-display text-xl font-bold text-slate-900">
                                Tilmeld dig launch
                            </h3>
                            <p className="text-sm text-slate-500 mt-0.5">
                                Bliv én af de første kunder — inkl. 14 dages gratis prøveperiode ved launch.
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="h-8 w-8 rounded-full hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center"
                            data-testid="waitlist-dialog-close"
                            aria-label="Luk"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {done ? (
                    <div className="px-7 py-8" data-testid="waitlist-dialog-success">
                        <div className="text-center">
                            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 text-2xl">
                                ✓
                            </div>
                            <h4 className="mt-4 font-display text-lg font-semibold text-slate-900">
                                Du er på ventelisten!
                            </h4>
                            <p className="mt-1.5 text-sm text-slate-600">
                                Vi sender dig en mail på <strong>{form.email}</strong> så snart Cleany er klar.
                            </p>
                        </div>

                        {/* Referral / skip the line */}
                        <div className="mt-6 rounded-xl bg-gradient-to-br from-sky-50 to-white border border-sky-200 p-4">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-sky-700">
                                ⚡ Inviter kolleger
                            </div>
                            <p className="mt-1 text-sm text-slate-700 leading-snug">
                                Del dit link i branchen — jo flere I er, desto bedre bliver Cleany.
                            </p>
                            {refCount !== null && (
                                <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="referral-count">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                    {refCount === 0
                                        ? "Endnu ingen tilmeldt via dit link"
                                        : `${refCount} ${refCount === 1 ? "person er" : "personer er"} tilmeldt via dit link`}
                                </div>
                            )}
                            <div className="mt-3 flex gap-2">
                                <input
                                    readOnly
                                    value={referralLink}
                                    onClick={(e) => e.target.select()}
                                    className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-mono text-slate-700 truncate"
                                    data-testid="referral-link-input"
                                />
                                <button
                                    onClick={() => {
                                        navigator.clipboard?.writeText(referralLink);
                                        toast.success("Link kopieret");
                                    }}
                                    data-testid="referral-copy"
                                    className="h-9 px-3 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold"
                                >
                                    Kopier
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="mt-5 w-full inline-flex justify-center rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium h-11 px-6"
                            data-testid="waitlist-dialog-close-success"
                        >
                            Færdig
                        </button>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="px-7 py-5 space-y-3" data-testid="waitlist-form">
                        {/* Honeypot — invisible, tricks bots into filling it. Real users don't see this. */}
                        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                            <label htmlFor="waitlist-hp-website">Website (lad stå tom)</label>
                            <input
                                id="waitlist-hp-website"
                                type="text"
                                name="website"
                                value={form.website}
                                onChange={update("website")}
                                tabIndex={-1}
                                autoComplete="off"
                            />
                        </div>
                        <Field
                            label="Navn"
                            placeholder="Fornavn Efternavn"
                            value={form.name}
                            onChange={update("name")}
                            testid="waitlist-input-name"
                            required
                        />
                        <Field
                            label="Firma"
                            placeholder="Dit rengøringsfirma"
                            value={form.company}
                            onChange={update("company")}
                            testid="waitlist-input-company"
                            required
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Field
                                label="Email"
                                type="email"
                                placeholder="navn@firma.dk"
                                value={form.email}
                                onChange={update("email")}
                                testid="waitlist-input-email"
                                required
                            />
                            <Field
                                label="Telefon"
                                placeholder="+45 ..."
                                value={form.phone}
                                onChange={update("phone")}
                                testid="waitlist-input-phone"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-700">Besked (valgfri)</label>
                            <textarea
                                value={form.message}
                                onChange={update("message")}
                                rows={3}
                                placeholder="Hvor mange medarbejdere har I? Hvilke moduler er vigtige for jer?"
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200 resize-none"
                                data-testid="waitlist-input-message"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="waitlist-input-ref"
                                className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5"
                            >
                                <Gift className="h-3.5 w-3.5 text-sky-600" />
                                Referral-kode (valgfri)
                            </label>
                            <div className="relative mt-1">
                                <input
                                    id="waitlist-input-ref"
                                    type="text"
                                    value={refCodeInput}
                                    onChange={(e) => setRefCodeInput(e.target.value)}
                                    placeholder="Fx 0jMO4Ab8"
                                    autoComplete="off"
                                    spellCheck="false"
                                    maxLength={32}
                                    data-testid="waitlist-input-ref"
                                    className={
                                        "w-full rounded-lg border bg-white px-3 py-2 pr-9 text-sm font-mono tracking-wider placeholder:text-slate-400 placeholder:font-sans placeholder:tracking-normal focus:outline-none focus:ring-2 transition-colors " +
                                        (refStatus.state === "valid"
                                            ? "border-emerald-400 focus:border-emerald-500 focus:ring-emerald-100"
                                            : refStatus.state === "invalid"
                                                ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                                                : "border-slate-300 focus:border-sky-500 focus:ring-sky-200")
                                    }
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center" data-testid={`waitlist-ref-status-${refStatus.state}`}>
                                    {refStatus.state === "checking" && (
                                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                    )}
                                    {refStatus.state === "valid" && (
                                        <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
                                    )}
                                    {refStatus.state === "invalid" && (
                                        <X className="h-4 w-4 text-rose-500" strokeWidth={3} />
                                    )}
                                </span>
                            </div>
                            {refStatus.state === "valid" && (
                                <p className="mt-1.5 text-[11px] text-emerald-700 font-medium" data-testid="ref-valid-msg">
                                    ✓ Gyldig kode{refStatus.referrer ? ` — henvist af ${refStatus.referrer}` : ""}.
                                </p>
                            )}
                            {refStatus.state === "invalid" && (
                                <p className="mt-1.5 text-[11px] text-rose-600 font-medium" data-testid="ref-invalid-msg">
                                    ✗ Ukendt kode. Tjek at den er stavet rigtigt.
                                </p>
                            )}
                            {refStatus.state === "idle" && (
                                <p className="mt-1.5 text-[11px] text-slate-500">
                                    Har du fået en kode af en kollega eller fra et delelink?
                                </p>
                            )}
                        </div>
                        <label className="flex items-start gap-2.5 pt-1 cursor-pointer select-none" data-testid="waitlist-privacy-row">
                            <input
                                type="checkbox"
                                checked={acceptPrivacy}
                                onChange={(e) => setAcceptPrivacy(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-sky-600 cursor-pointer shrink-0"
                                data-testid="waitlist-input-privacy"
                            />
                            <span className="text-[12px] text-slate-600 leading-snug">
                                Jeg accepterer at Cleany behandler mine oplysninger ifm.
                                ventelisten i henhold til{" "}
                                <a
                                    href="/privatlivspolitik"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sky-700 hover:text-sky-900 underline underline-offset-2 font-medium"
                                >
                                    privatlivspolitikken
                                </a>
                                .
                            </span>
                        </label>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium h-11 px-5 shadow-lg shadow-sky-600/20 transition-all active:scale-[0.98]"
                            data-testid="waitlist-submit"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" /> Sender...
                                </>
                            ) : (
                                <>
                                    Tilmeld mig <ArrowRight className="h-4 w-4" />
                                </>
                            )}
                        </button>
                        <p className="text-[11px] text-slate-500 text-center pt-1">
                            Vi kontakter dig kun ifm. launch. GDPR & ingen spam.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({ label, type = "text", testid, ...props }) {
    return (
        <div>
            <label className="text-xs font-semibold text-slate-700">{label}</label>
            <input
                type={type}
                data-testid={testid}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                {...props}
            />
        </div>
    );
}
